import { useContext } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  useEvaluatedAttributes,
} from '@ea-lab/reactive-json'
import { CX, CY, HEIGHT, LABEL_GAP, R, WIDTH, clampRatio, polygon, vertex, wrapLabel } from './radarGeometry'

/**
 * Primitive GÉNÉRIQUE — un radar (toile d'araignée) en SVG en ligne.
 *
 * Pourquoi du JSX : les sommets d'un polygone régulier se calculent en
 * trigonométrie, et le YAML ne calcule pas.
 *
 * Pourquoi PAS `ChartJs` (qui sait faire un radar avec `RadialLinearScale`, et
 * dont la bibliothèque est de toute façon déjà dans le lot) : un canvas ne se
 * style pas en CSS — l'habillage devrait passer en JS, alors qu'ici le tracé ne
 * porte que des classes et AUCUNE couleur n'est écrite dans ce fichier ; et il
 * ne porte pas d'alternative textuelle native. Un radar de restitution est un
 * élément de PAGE, relevant du design system, pas un graphique de tableau de bord.
 *
 * Usage YAML :
 *   - type: RadarChart
 *     axes: ~~.quizResult.dimensions     # [{label, ratio}] — ratio de 0 à 1
 *     label: ~~.quizResult.summary       # alternative textuelle du graphique
 *     levels: 4
 *     attributes: { class: "mon-radar" }   # l'habillage vient de la recette CSS du site
 *
 * Accessibilité : `role="img"` + `aria-label`. Le graphique ne doit JAMAIS
 * porter seul une information — ici les mêmes valeurs sont listées en toutes
 * lettres juste en dessous.
 */

export const RadarChart = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const raw = resolve(props.axes)
  const axes = Array.isArray(raw) ? raw : []
  const levels = Number(resolve(props.levels) ?? 4)
  const label = resolve(props.label) ?? ''
  const evaluatedAttrs = useEvaluatedAttributes(props.attributes ?? {})

  // Trois axes au minimum : en dessous, un « polygone » est un segment.
  if (axes.length < 3) return null

  const n = axes.length
  // Le sommet d'une valeur nulle est ramené sur un rayon minuscule : sans lui,
  // un profil à zéro s'effondre sur le centre et le tracé disparaît.
  const values = axes.map((a, i) => vertex(i, n, R * Math.max(clampRatio(a.ratio), 0.03)))

  return (
    <ActionDependant {...props}>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label={label || undefined}
        aria-hidden={label ? undefined : 'true'}
        {...evaluatedAttrs}
      >
        {Array.from({ length: levels }, (_, i) => (i + 1) / levels).map((f) => (
          <polygon key={f} className="radar-chart__grid" points={polygon(n, R * f)} fill="none" />
        ))}
        {axes.map((axis, i) => {
          const [x, y] = vertex(i, n, R)
          return (
            <line key={axis.key ?? i} className="radar-chart__axis" x1={CX} y1={CY} x2={x.toFixed(1)} y2={y.toFixed(1)} />
          )
        })}
        <polygon
          className="radar-chart__area"
          points={values.map((v) => v.map((x) => x.toFixed(1)).join(',')).join(' ')}
          strokeLinejoin="round"
        />
        {values.map((v, i) => (
          <circle key={axes[i].key ?? i} className="radar-chart__dot" cx={v[0].toFixed(1)} cy={v[1].toFixed(1)} r="4.5" />
        ))}
        {axes.map((axis, i) => {
          const [x, y] = vertex(i, n, R + LABEL_GAP)
          const anchor = Math.abs(x - CX) < 12 ? 'middle' : x > CX ? 'start' : 'end'
          const tx = anchor === 'start' ? x - 6 : anchor === 'end' ? x + 6 : x
          const lines = wrapLabel(axis.label)
          const y0 = y + 4 - (lines.length - 1) * 8
          return lines.map((line, li) => (
            <text
              key={`${axis.key ?? i}-${li}`}
              className="radar-chart__label"
              x={tx.toFixed(0)}
              y={(y0 + li * 16).toFixed(0)}
              textAnchor={anchor}
            >
              {line}
            </text>
          ))
        })}
      </svg>
    </ActionDependant>
  )
}
